/* eslint-disable @typescript-eslint/no-invalid-void-type */
import { createApi } from "@reduxjs/toolkit/query/react";

import { Contact } from "src/types/Contact";
import Sort from "src/types/Sort";
import {
  CNCSubmission,
  InventorySubmission,
  SubmissionStatus,
  SubmissionStatuses,
} from "src/types/Submission";
import SubmissionPeriod from "src/types/SubmissionPeriod";
import ServiceLine from "src/types/ServiceLine";
import System from "src/types/System";
import { Activity } from "src/types/Activity";
import baseQuery from "./baseQuery";
import { Tag, TagLabel } from "./tags";
import buildParameters, { PaginatedResponse, ApiResponse } from "./utils";
import Territory from "src/types/Territory";
import { SavedView } from "src/types/SavedView";
import { SubmissionCommunication } from "src/types/SubmissionCommunication";

export type ValidFilterValue =
  | undefined
  | Array<boolean | number | string>
  | boolean
  | number
  | string;
export type Filters = Record<string, undefined | ValidFilterValue>;

export interface SearchParams {
  filters: Filters;
  include?: string;
  page: number;
  pageSize: number;
  searchTerm?: string;
  sort?: Sort | string;
}

export interface ResponseList<T> {
  data: T[];
}

export interface IndividualSystem {
  data: System;
}

interface ContactParams {
  contactId: number;
  systemId: number;
}

interface SubmissionsCountBySubmissionPeriodPwsAndValidStatusParams {
  submissionPeriodId: number;
  pwsId: number;
}

interface GroupedStatusCountsParams {
  submissionPeriodId: number;
}

interface CreateContact {
  contact?: Omit<Partial<Contact>, "id">;
  systemId: number;
}

interface UpdateContact {
  contact?: Omit<Partial<Contact>, "id">;
  contactId: number;
  systemId: number;
}

interface CreateSubmissionPeriod {
  submissionPeriod?: Omit<Partial<SubmissionPeriod>, "id">;
}

interface UpdateSubmissionPeriod {
  submissionPeriod?: Omit<Partial<SubmissionPeriod>, "id">;
  submissionPeriodId: number;
}

interface DeleteSubmissionPeriod {
  submissionPeriodId: number;
}

interface UpdateSubmissionStatus {
  submissionId: string;
  submissionStatus: string;
}

interface UpdateSubmissionPeriodSystems {
  submissionPeriodId: number;
  systemIds: number[];
}

interface ServiceLineParams {
  submissionId: string;
  paramsData: SearchParams;
}

interface GetAllSubmissionsParams {
  submissionPeriodId?: number;
  searchParams: SearchParams;
}

interface ServiceLineSingleParams {
  submissionId: string;
  serviceLineId: string;
}

interface SubmissionStatusUpdate {
  id: number;
  status: string;
}

interface SubmissionStatusUpdateResponse {
  data: {
    updatedSubmissions: InventorySubmission[];
    submissionsNotUpdated: string[];
  };
}

interface MarkNotOverdueAndLockResponse {
  data: {
    updatedSubmission: InventorySubmission;
  };
}
interface BulkSubmissionPeriodUpdateResponse {
  data: {
    updatedSystems: System[];
    systemsNotUpdated: string[];
  };
}

interface SubmissionPeriodCommentParams {
  submissionPeriodId?: number;
  pwsId: number;
}
interface CommentFormData {
  comment: string;
  commentType: string;
  originType: string;
  originId: number;
  submissionId: number;
}

interface CreateComment {
  submissionPeriodId: number;
  pwsId: number;
  comment: CommentFormData;
}

interface GetPwsSubmissionPeriodsParams {
  submissionPeriodId: number;
  searchParams: SearchParams;
}

export interface PrimacyAgency {
  id: number;
  primacyCode: string;
  name: string;
  accountId: number;
  createdOn: string;
  createdBy: string;
  modifiedOn: string;
  modifiedBy: string;
  extent: FullExtent;
  centroid: Centroid;
}

export interface FullExtent {
  type: string;
  coordinates: number[][][];
}

export interface Centroid {
  type: string;
  coordinates: number[];
}

export interface StatusCount {
  status: SubmissionStatuses | "statusesTotal";
  count: number;
  statusesTotal?: number;
}

export interface PwsSubmissionPeriod extends Record<string, unknown> {
  pwsId: number;
  submissionPeriodId: number;
  active: boolean;
  status: SubmissionStatuses;
  activeSubmissionId: number;
  submissionAttemptCount: number;
  system: System;
  submissionPeriod: SubmissionPeriod;
  activeSubmission: InventorySubmission | CNCSubmission | null;
}

export interface InventoryPwsSubmissionPeriod extends PwsSubmissionPeriod {
  activeSubmission: InventorySubmission;
  // TODO: This is deprecated and won't be included with new CNC Submission types
  submissionCommunication: SubmissionCommunication;
}

export interface CncPwsSubmissionPeriod extends PwsSubmissionPeriod {
  activeSubmission: CNCSubmission;
}

export const stateSubmissionService = createApi({
  baseQuery: baseQuery({ baseUrl: "/pws/state-submission" }),
  reducerPath: "statesubmission",
  tagTypes: [
    Tag.Comments,
    Tag.Counties,
    Tag.Contacts,
    Tag.ServiceLines,
    Tag.SubmissionPeriods,
    Tag.Submissions,
    Tag.Systems,
    Tag.PwsSubmissionPeriods,
    Tag.PwsSubmissionPeriodStatuses,
    Tag.SavedViews,
    Tag.SubmissionCommunications,
    Tag.Territories,
  ],
  endpoints: (builder) => ({
    createContact: builder.mutation<Contact, CreateContact>({
      invalidatesTags: [{ id: TagLabel.List, type: Tag.Contacts }],
      query: ({ contact, systemId }) => {
        return {
          body: contact,
          method: "POST",
          url: `/systems/${systemId}/contacts`,
        };
      },
    }),
    deleteContact: builder.mutation<void, ContactParams>({
      invalidatesTags: [{ id: TagLabel.List, type: Tag.Contacts }],
      query: ({ contactId, systemId }) => ({
        method: "delete",
        url: `/systems/${systemId}/contacts/${contactId}`,
      }),
    }),
    editContact: builder.mutation<Contact, UpdateContact>({
      invalidatesTags: [{ id: TagLabel.List, type: Tag.Contacts }],
      query: ({ contact, contactId, systemId }) => ({
        body: contact,
        method: "put",
        url: `/systems/${systemId}/contacts/${contactId}`,
      }),
    }),
    getSubmissionPeriod: builder.query<ApiResponse<SubmissionPeriod>, string>({
      providesTags: [{ id: TagLabel.List, type: Tag.Submissions }],
      query: (submissionPeriodId) => {
        return {
          method: "GET",
          url: `/submission-periods/${submissionPeriodId}`,
        };
      },
    }),
    getAllSubmissionPeriods: builder.query<
      PaginatedResponse<SubmissionPeriod>,
      SearchParams
    >({
      providesTags: [{ id: TagLabel.List, type: Tag.SubmissionPeriods }],
      query: (paramsObject) => {
        const params = buildParameters(paramsObject).toString();
        return {
          method: "GET",
          url: `/submission-periods?${params}`,
        };
      },
    }),
    getAllActiveSubmissionPeriods: builder.query<
      ApiResponse<SubmissionPeriod[]>,
      void
    >({
      providesTags: [{ id: TagLabel.List, type: Tag.SubmissionPeriods }],
      query: () => {
        return {
          method: "GET",
          url: `/active-submission-periods`,
        };
      },
      // Add the locked field for all entries
      transformResponse: (response: ApiResponse<SubmissionPeriod[]>) => {
        const currentDate = new Date();

        const transformedData = response.data.map((period) => {
          const lockedDate = period.lockedDate
            ? new Date(period.lockedDate)
            : null;
          const locked = lockedDate ? lockedDate <= currentDate : false;

          return {
            ...period,
            locked,
          };
        });

        return {
          ...response,
          data: transformedData,
        };
      },
    }),
    getAllActiveSubmissionPeriodsForSystem: builder.query<
      ApiResponse<SubmissionPeriod[]>,
      number
    >({
      providesTags: [{ id: TagLabel.List, type: Tag.SubmissionPeriods }],
      query: (systemId) => {
        return {
          method: "GET",
          url: `/submission-periods/active-all?systemId=${systemId}`,
        };
      },
    }),
    getTotalSubmissionsCountBySubmissionPeriodPwsAndValidStatus: builder.query<
      ApiResponse<number>,
      SubmissionsCountBySubmissionPeriodPwsAndValidStatusParams
    >({
      query: ({ submissionPeriodId, pwsId }) => {
        return {
          method: "GET",
          url: `/submissions/submission-period/${submissionPeriodId}/pws-id/${pwsId}/submission-count`,
        };
      },
    }),
    getGroupedStatusCounts: builder.query<
      ApiResponse<StatusCount[]>,
      GroupedStatusCountsParams
    >({
      providesTags: [
        { id: TagLabel.List, type: Tag.PwsSubmissionPeriodStatuses },
      ],
      query: ({ submissionPeriodId }) => {
        return {
          method: "GET",
          url: `/pws-submission-period/${submissionPeriodId}/grouped-submission-count`,
        };
      },
    }),
    getAllInventorySubmissions: builder.query<
      PaginatedResponse<InventorySubmission>,
      GetAllSubmissionsParams
    >({
      providesTags: [{ id: TagLabel.List, type: Tag.Submissions }],
      query: ({ submissionPeriodId, searchParams }) => {
        const params = buildParameters(searchParams).toString();
        return {
          method: "GET",
          url: `/sli/submissions/submission-period/${
            submissionPeriodId ?? 0
          }?${params}`,
        };
      },
    }),
    getAllCNCSubmissions: builder.query<
      PaginatedResponse<CNCSubmission>,
      GetAllSubmissionsParams
    >({
      providesTags: [{ id: TagLabel.List, type: Tag.Submissions }],
      query: ({ submissionPeriodId, searchParams }) => {
        const params = buildParameters(searchParams).toString();
        return {
          method: "GET",
          url: `/cnc/submissions/submission-period/${
            submissionPeriodId ?? 0
          }?${params}`,
        };
      },
    }),
    getAllSubmissionPeriodComments: builder.query<
      ApiResponse<Activity[]>,
      SubmissionPeriodCommentParams
    >({
      providesTags: [{ id: TagLabel.List, type: Tag.Comments }],
      query: ({ submissionPeriodId, pwsId }) => {
        return {
          method: "GET",
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          url: `/submission-periods/${submissionPeriodId}/comments/${pwsId}`,
        };
      },
    }),
    createComment: builder.mutation<Activity, CreateComment>({
      invalidatesTags: [{ id: TagLabel.List, type: Tag.Comments }],
      query: ({ submissionPeriodId, pwsId, comment }) => {
        return {
          body: comment,
          method: "POST",
          url: `/submission-periods/${submissionPeriodId}/comments/${pwsId}`,
        };
      },
    }),
    getAllSubmissionServiceLines: builder.query<
      PaginatedResponse<ServiceLine>,
      ServiceLineParams
    >({
      providesTags: [{ id: TagLabel.List, type: Tag.ServiceLines }],
      query: ({ submissionId, paramsData }) => {
        const params = buildParameters(paramsData).toString();
        return {
          method: "GET",
          url: `/submissions/${submissionId}/servicelines?${params}`,
        };
      },
    }),
    getSubmissionServiceLine: builder.query<
      ServiceLine,
      ServiceLineSingleParams
    >({
      providesTags: [{ id: TagLabel.List, type: Tag.ServiceLines }],
      query: ({ submissionId, serviceLineId }) => {
        return {
          method: "GET",
          url: `/submissions/${submissionId}/servicelines/${serviceLineId}`,
        };
      },
    }),
    getInventorySubmission: builder.query<
      ApiResponse<InventorySubmission>,
      string
    >({
      providesTags: [{ id: TagLabel.List, type: Tag.Submissions }],
      query: (submissionId) => {
        return {
          method: "GET",
          url: `/sli/submissions/${submissionId}`,
        };
      },
    }),
    getCNCSubmission: builder.query<ApiResponse<CNCSubmission>, string>({
      providesTags: [{ id: TagLabel.List, type: Tag.Submissions }],
      query: (submissionId) => {
        return {
          method: "GET",
          url: `/cnc/submissions/${submissionId}`,
        };
      },
    }),
    getLatestInventorySubmission: builder.query<
      ApiResponse<InventorySubmission>,
      string
    >({
      providesTags: [{ id: TagLabel.List, type: Tag.Submissions }],
      query: (systemId) => {
        return {
          method: "GET",
          url: `/sli/submissions/systems/${systemId}/latest`,
        };
      },
    }),
    getLatestCNCSubmission: builder.query<ApiResponse<CNCSubmission>, string>({
      providesTags: [{ id: TagLabel.List, type: Tag.Submissions }],
      query: (systemId) => {
        return {
          method: "GET",
          url: `/cnc/submissions/systems/${systemId}/latest`,
        };
      },
    }),
    getAllSystemContacts: builder.query<
      PaginatedResponse<Contact>,
      string | number
    >({
      providesTags: [{ id: TagLabel.List, type: Tag.Contacts }],
      query: (systemId) => `/systems/${systemId}/contacts`,
    }),
    getAllSystems: builder.query<ResponseList<System>, Partial<SearchParams>>({
      providesTags: [{ id: TagLabel.List, type: Tag.Systems }],
      query: (paramsObject) => {
        const params = buildParameters(paramsObject).toString();

        return {
          method: "get",
          url: `/systems/all?${params}`,
        };
      },
    }),
    getContact: builder.query<Contact, ContactParams>({
      providesTags: (result) =>
        result
          ? [{ id: result.id, type: Tag.Contacts }]
          : [{ type: Tag.Contacts }],
      query: ({ contactId, systemId }) =>
        `/systems/${systemId}/contacts/${contactId}`,
    }),
    getPagedSystems: builder.query<PaginatedResponse<System>, SearchParams>({
      providesTags: [{ id: TagLabel.List, type: Tag.Systems }],
      query: (paramsObject) => {
        const params = buildParameters(paramsObject).toString();

        return {
          method: "get",
          url: `/systems?${params}`,
        };
      },
    }),
    getSingleSystem: builder.query<IndividualSystem, string>({
      providesTags: (result) =>
        result
          ? [{ id: result.data.id, type: Tag.Systems }]
          : [{ type: Tag.Systems }],
      query: (id) => `/systems/${id}`,
    }),
    createSubmissionPeriod: builder.mutation<
      SubmissionPeriod,
      CreateSubmissionPeriod
    >({
      invalidatesTags: [{ id: TagLabel.List, type: Tag.Contacts }],
      query: ({ submissionPeriod }) => {
        return {
          body: submissionPeriod,
          method: "POST",
          url: `/submission-periods`,
        };
      },
    }),
    updateSubmissionPeriod: builder.mutation<
      SubmissionPeriod,
      UpdateSubmissionPeriod
    >({
      invalidatesTags: [
        { id: TagLabel.List, type: Tag.SubmissionPeriods },
        { id: TagLabel.List, type: Tag.Systems },
      ],
      query: ({ submissionPeriod, submissionPeriodId }) => ({
        body: submissionPeriod,
        method: "PUT",
        url: `/submission-periods/${submissionPeriodId}`,
      }),
    }),
    deleteSubmissionPeriod: builder.mutation<void, DeleteSubmissionPeriod>({
      invalidatesTags: [
        { id: TagLabel.List, type: Tag.SubmissionPeriods },
        { id: TagLabel.List, type: Tag.Systems },
      ],
      query: ({ submissionPeriodId }) => ({
        body: {},
        method: "DELETE",
        url: `/submission-periods/${submissionPeriodId}`,
      }),
    }),
    upsertSubmissionPeriod: builder.mutation<
      SubmissionPeriod,
      Omit<SubmissionPeriod, "id"> & { id?: number }
    >({
      invalidatesTags: [
        { id: TagLabel.List, type: Tag.SubmissionPeriods },
        { id: TagLabel.List, type: Tag.Systems },
      ],
      query: (submissionPeriod) => ({
        body: submissionPeriod,
        method: submissionPeriod.id ? "PUT" : "POST",
        url: submissionPeriod.id
          ? `/submission-periods/${submissionPeriod.id}`
          : "/submission-periods",
      }),
    }),
    addSystemsToSubmissionPeriod: builder.mutation<
      BulkSubmissionPeriodUpdateResponse,
      UpdateSubmissionPeriodSystems
    >({
      invalidatesTags: [
        { id: TagLabel.List, type: Tag.SubmissionPeriods },
        { id: TagLabel.List, type: Tag.Systems },
      ],
      query: ({ submissionPeriodId, systemIds }) => ({
        body: { data: systemIds },
        method: "put",
        url: `/submission-periods/${submissionPeriodId}/systems`,
      }),
    }),
    removeSystemsFromSubmissionPeriod: builder.mutation<
      BulkSubmissionPeriodUpdateResponse,
      UpdateSubmissionPeriodSystems
    >({
      invalidatesTags: [
        { id: TagLabel.List, type: Tag.SubmissionPeriods },
        { id: TagLabel.List, type: Tag.Systems },
      ],
      query: ({ submissionPeriodId, systemIds }) => ({
        body: { data: systemIds },
        method: "delete",
        url: `/submission-periods/${submissionPeriodId}/systems`,
      }),
    }),
    updateSubmission: builder.mutation<
      SubmissionStatus,
      UpdateSubmissionStatus
    >({
      invalidatesTags: [
        { id: TagLabel.List, type: Tag.Submissions },
        { id: TagLabel.List, type: Tag.PwsSubmissionPeriods },
        { id: TagLabel.List, type: Tag.PwsSubmissionPeriodStatuses },
      ],
      query: ({ submissionId, submissionStatus }) => ({
        body: { status: submissionStatus },
        method: "put",
        url: `/submissions/${submissionId}`,
      }),
    }),
    updateSubmissionStatuses: builder.mutation<
      SubmissionStatusUpdateResponse,
      SubmissionStatusUpdate[]
    >({
      invalidatesTags: [
        { id: TagLabel.List, type: Tag.Submissions },
        { id: TagLabel.List, type: Tag.PwsSubmissionPeriods },
        { id: TagLabel.List, type: Tag.PwsSubmissionPeriodStatuses },
      ],
      query: (submissionStatusArray) => ({
        body: { data: submissionStatusArray },
        method: "put",
        url: `/submissions/bulkupdatestatus`,
      }),
    }),
    getServiceLineMaterialOptions: builder.query<ResponseList<string>, {}>({
      query: () => "/submissions/servicelines/material_selection_options",
    }),
    getCurrentPrimacyAgency: builder.query<PrimacyAgency, undefined>({
      query: () => ({
        method: "get",
        url: "/primacy-agency",
      }),
    }),
    getPwsInventorySubmissionPeriods: builder.query<
      PaginatedResponse<PwsSubmissionPeriod>,
      GetPwsSubmissionPeriodsParams
    >({
      providesTags: [{ id: TagLabel.List, type: Tag.PwsSubmissionPeriods }],
      query: ({ submissionPeriodId, searchParams }) => {
        const params = buildParameters(searchParams).toString();

        return {
          method: "get",
          url: `/pws-sli-submission-period/${submissionPeriodId}?${params}`,
        };
      },
    }),
    getPwsCNCSubmissionPeriods: builder.query<
      PaginatedResponse<PwsSubmissionPeriod>,
      GetPwsSubmissionPeriodsParams
    >({
      providesTags: [{ id: TagLabel.List, type: Tag.PwsSubmissionPeriods }],
      query: ({ submissionPeriodId, searchParams }) => {
        const params = buildParameters(searchParams).toString();

        return {
          method: "get",
          url: `/pws-cnc-submission-period/${submissionPeriodId}?${params}`,
        };
      },
    }),
    getAllTerritories: builder.query<Territory[], {}>({
      providesTags: [{ id: TagLabel.List, type: Tag.Territories }],
      query: () => "/territories",
    }),
    addUpdateTerritory: builder.mutation<string, {}>({
      invalidatesTags: [{ id: TagLabel.List, type: Tag.Territories }],
      query: (data: { name: string; updateId: number }) => {
        const { name, updateId } = data;
        const territoryId = updateId ? `/${updateId}` : "";
        const method = updateId ? "put" : "post";
        const url = `/territories${territoryId}`;
        return {
          method,
          url,
          body: { name },
        };
      },
    }),
    addBulkSystemsTerritories: builder.mutation<string, {}>({
      query: (data: { systemIds: number[]; territoryId: number }) => {
        const { systemIds, territoryId } = data;
        return {
          method: "put",
          url: `/territories/${territoryId}/systems`,
          body: systemIds,
        };
      },
    }),
    deleteTerritory: builder.mutation<string, {}>({
      invalidatesTags: [{ id: TagLabel.List, type: Tag.Territories }],
      query: (data: { territoryId: number }) => {
        const { territoryId } = data;
        return {
          method: "delete",
          url: `/territories/${territoryId}`,
        };
      },
    }),
    getSystemsCountByTerritoryId: builder.query<number, number>({
      query: (territoryId) => {
        return {
          method: "get",
          url: `/territories/${territoryId}/systems/count`,
        };
      },
    }),
    removeBulkSystemsTerritories: builder.mutation<string, {}>({
      query: (data: { systemIds: number[] }) => {
        const { systemIds } = data;
        return {
          method: "delete",
          url: `/territories/systems`,
          body: systemIds,
        };
      },
    }),
    getSavedViews: builder.query<SavedView[], {}>({
      providesTags: [{ id: TagLabel.List, type: Tag.SavedViews }],
      query: ({ tableName }: { tableName: string }) =>
        `/saved-views/${tableName}`,
    }),
    createSavedView: builder.mutation<SavedView, {}>({
      invalidatesTags: [{ id: TagLabel.List, type: Tag.SavedViews }],
      query: (
        data: Pick<
          SavedView,
          "name" | "ownerType" | "ownerId" | "view" | "tableName"
        >
      ) => {
        return {
          method: "post",
          url: "/saved-views",
          body: {
            ...data,
            view: JSON.stringify(data.view),
          },
        };
      },
    }),
    editSavedView: builder.mutation<SavedView, {}>({
      invalidatesTags: [{ id: TagLabel.List, type: Tag.SavedViews }],
      query: (data: { id: number; view: Record<string, unknown> }) => {
        return {
          method: "put",
          url: `/saved-views/${data.id}`,
          body: { view: JSON.stringify(data.view) },
        };
      },
    }),
    deleteSavedView: builder.mutation<SavedView, {}>({
      invalidatesTags: [{ id: TagLabel.List, type: Tag.SavedViews }],
      query: (data: { id: number }) => {
        return {
          method: "delete",
          url: `/saved-views/${data.id}`,
        };
      },
    }),
    updateSubmissionCommunication: builder.mutation<
      SubmissionStatusUpdateResponse,
      { submissionCommunicationId: number; value: string }
    >({
      invalidatesTags: [
        { id: TagLabel.List, type: Tag.SubmissionCommunications },
      ],
      query: ({ submissionCommunicationId, value }) => ({
        body: {
          submissionCommunicationId,
          status: value,
        },
        method: "put",
        url: `/communications/${submissionCommunicationId}`,
      }),
    }),
    createSubmissionCommunication: builder.mutation<
      SubmissionStatusUpdateResponse,
      { value: string; pwsId: number; submissionPeriodId: number }
    >({
      invalidatesTags: [
        { id: TagLabel.List, type: Tag.SubmissionCommunications },
      ],
      query: ({ value, pwsId, submissionPeriodId }) => ({
        body: value,
        method: "post",
        url: `/communications/${pwsId}/${submissionPeriodId}`,
      }),
    }),
    assignSubmissionAssignees: builder.mutation<
      InventorySubmission,
      { assignUsersDtos: Array<{ submissionId: number; userId: number }> }
    >({
      invalidatesTags: [
        { id: TagLabel.List, type: Tag.Submissions },
        { id: TagLabel.List, type: Tag.PwsSubmissionPeriods },
      ],
      query: (data) => ({
        method: "post",
        url: `/submissions/assignUsers`,
        body: data,
      }),
    }),
    unassignSubmissionAssignees: builder.mutation<
      InventorySubmission,
      number[]
    >({
      invalidatesTags: [
        { id: TagLabel.List, type: Tag.Submissions },
        { id: TagLabel.List, type: Tag.PwsSubmissionPeriods },
      ],
      query: (submissionIds) => ({
        method: "post",
        url: `/submissions/unassignUsers`,
        body: {
          submissionIds,
        },
      }),
    }),
    updateSubmissionToNotOverdueAndLock: builder.mutation<
      MarkNotOverdueAndLockResponse,
      { submissionId: number }
    >({
      invalidatesTags: [
        { id: TagLabel.List, type: Tag.SubmissionCommunications },
      ],
      query: ({ submissionId }) => ({
        body: {},
        method: "PUT",
        url: `/submissions/lock-overdue/${submissionId}`,
      }),
    }),
  }),
});

export const {
  useRemoveSystemsFromSubmissionPeriodMutation,
  useAddSystemsToSubmissionPeriodMutation,
  useCreateContactMutation,
  useCreateCommentMutation,
  useDeleteContactMutation,
  useEditContactMutation,
  useGetInventorySubmissionQuery,
  useGetAllInventorySubmissionsQuery,
  useGetAllCNCSubmissionsQuery,
  useGetSubmissionPeriodQuery,
  useGetAllSubmissionPeriodsQuery,
  useGetAllActiveSubmissionPeriodsQuery,
  useGetAllActiveSubmissionPeriodsForSystemQuery,
  useGetTotalSubmissionsCountBySubmissionPeriodPwsAndValidStatusQuery,
  useGetGroupedStatusCountsQuery,
  useGetAllSubmissionPeriodCommentsQuery,
  useGetAllSubmissionServiceLinesQuery,
  useGetSubmissionServiceLineQuery,
  useGetAllSystemContactsQuery,
  useGetAllSystemsQuery,
  useGetContactQuery,
  useGetLatestInventorySubmissionQuery,
  useGetLatestCNCSubmissionQuery,
  useGetPagedSystemsQuery,
  useGetSingleSystemQuery,
  useCreateSubmissionPeriodMutation,
  useUpdateSubmissionPeriodMutation,
  useDeleteSubmissionPeriodMutation,
  useUpsertSubmissionPeriodMutation,
  useUpdateSubmissionMutation,
  useUpdateSubmissionStatusesMutation,
  useGetServiceLineMaterialOptionsQuery,
  useGetCurrentPrimacyAgencyQuery,
  useGetPwsInventorySubmissionPeriodsQuery,
  useGetPwsCNCSubmissionPeriodsQuery,
  useGetAllTerritoriesQuery,
  useAddUpdateTerritoryMutation,
  useAddBulkSystemsTerritoriesMutation,
  useRemoveBulkSystemsTerritoriesMutation,
  useDeleteTerritoryMutation,
  useGetSystemsCountByTerritoryIdQuery,
  useGetSavedViewsQuery,
  useCreateSavedViewMutation,
  useEditSavedViewMutation,
  useDeleteSavedViewMutation,
  useUpdateSubmissionCommunicationMutation,
  useCreateSubmissionCommunicationMutation,
  useAssignSubmissionAssigneesMutation,
  useUnassignSubmissionAssigneesMutation,
  useUpdateSubmissionToNotOverdueAndLockMutation,
} = stateSubmissionService;

export default stateSubmissionService;
